import { useTranslation } from "@redotech/redo-i18n";
import { InteractiveMapLocation } from "@redotech/redo-model/draft-return/draft-return";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useQuery } from "@tanstack/react-query";
import { memo, useState } from "react";
import { InteractiveMap } from "./in-store-locations";

export interface BlueYonderLocationsSearchParams {
  draftReturnId?: string;
  returnId?: string;
}

const BlueYonderLocationsModal = memo(function BlueYonderLocationsModal({
  pending,
  error,
  locations,
}: {
  pending: boolean;
  error: boolean;
  locations: InteractiveMapLocation[] | null;
}) {
  const { t } = useTranslation();

  if (pending) {
    return <LoadingRedoAnimation />;
  }
  if (error) {
    return (
      <Text textColor="error">
        {t("Error loading FedEx Easy Returns locations")}
      </Text>
    );
  }
  if (!locations) {
    return null;
  }

  return <InteractiveMap locations={locations} />;
});

export const BlueYonderLocationsButton = memo(
  function BlueYonderLocationsButton({
    draftReturnId,
    returnId,
    autoOpen = false,
    searchBlueYonderLocations,
  }: {
    draftReturnId?: string;
    returnId?: string;
    autoOpen?: boolean;
    searchBlueYonderLocations: (
      params: BlueYonderLocationsSearchParams,
    ) => Promise<InteractiveMapLocation[]>;
  }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(autoOpen);
    const query = useQuery({
      queryKey: ["blueYonderLocations", draftReturnId, returnId],
      queryFn: () => searchBlueYonderLocations({ draftReturnId, returnId }),
      enabled: isOpen,
      staleTime: Infinity,
    });

    return (
      <Flex>
        <RedoButton
          hierarchy="secondary"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
          text={t("See available locations")}
        />
        <RedoModal
          isOpen={isOpen}
          onModalCloseRequested={() => setIsOpen(false)}
          title={t("Find FedEx Easy Returns drop-off locations")}
        >
          <BlueYonderLocationsModal
            error={!!query.error}
            locations={query.data ?? null}
            pending={query.isPending && query.fetchStatus !== "idle"}
          />
        </RedoModal>
      </Flex>
    );
  },
);
